/** @jsxImportSource @emotion/react */
import React from "react";
import Icon, { IconName } from "src/components/ui/icons";
import Text from "src/components/ui/text";
import { useTheme, useThemedCss } from "src/hooks/styles";
import { useMenuActive } from "src/hooks/menu";
import styles, { IStyles } from "src/components/ui/menu/MenuItem.styles";
import { useTutorialStore } from "src/store/tutorial";
import { Badge } from "src/components/ui/badge";
import { useAuthStore } from "src/store/account";
import { useBooleanState } from "src/hooks/boolean";
import { useLocation } from "react-router-dom";
import { useNextPathStore } from "src/store/redirect-context";
import { Dialog } from "src/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { LoginPopup, SignUpPopup } from "src/components/auth";

interface MenuItemProps {
  id?: string;
  onClick?: () => void;
  to?: string;
  title: string;
  isOpen: boolean;
  icon?: React.ReactNode;
  iconName?: IconName;
  className?: string;
  menuLevel?: 1 | 2 | 3 | 4;
  free?: boolean;
  authNeeded?: boolean;
}

const MenuItem: React.FC<MenuItemProps> = ({
  id,
  className,
  onClick,
  to,
  title,
  iconName,
  isOpen,
  menuLevel = 1,
  icon,
  free = false,
  authNeeded = false,
}) => {
  const isActive = useMenuActive(to, menuLevel);
  const { isAuthenticated } = useAuthStore();
  const [isLogin, setIsLogin] = React.useState(false);
  const { state: isModalShow, setTrue: showModal, setFalse: closeModal } = useBooleanState(false);
  const location = useLocation();
  const { setNextPath, nextPath } = useNextPathStore();

  const {
    menu: { iconSize },
  } = useTheme();
  const themedCss = useThemedCss<IStyles>(styles);

  const { setReadyComponents } = useTutorialStore();

  React.useEffect(() => {
    if (isAuthenticated) {
      closeModal();
    }
  }, [isAuthenticated, closeModal]);

  const handleAuthClick: React.MouseEventHandler = (event) => {
    event.stopPropagation();
    event.preventDefault();
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    setNextPath(currentUrl);
    showModal();
    return false;
  };

  React.useEffect(() => {
    if (id) {
      setReadyComponents([`#${id}`]);
    }
  }, [id]);

  const themedLinkCss = React.useMemo(() => {
    const css = [themedCss.link];
    if (isActive) {
      css.push(themedCss.linkActive);
    }
    if (isOpen) {
      css.push(themedCss.linkOpen);
    } else {
      css.push(themedCss.linkClosed);
    }
    return css;
  }, [isActive, isOpen]);

  const linkContent = (
    <div className="flex flex-row items-center justify-center">
      {iconName && (
        <Icon
          id={id}
          name={iconName}
          variant="inherit"
          css={isOpen ? themedCss.icon : undefined}
          size={iconSize}
          style={{
            minWidth: iconSize,
            maxWidth: iconSize,
            opacity: authNeeded && !isAuthenticated ? 0.5 : 1,
          }}
        />
      )}
      {icon && icon}
      {isOpen && (
        <div className="flex items-center justify-between w-full">
          <Text inherit css={themedCss.text} className="ml-2" style={{ opacity: authNeeded && !isAuthenticated ? 0.5 : 1 }}>
            {title}
          </Text>
          <div className="flex items-center gap-3">
            {free && (
              <Badge variant="secondary" className="text-[10px] px-2.5 font-medium bg-accent text-accent-foreground hover:bg-accent/70">
                Free
              </Badge>
            )}
            {authNeeded && !isAuthenticated && <Icon name="lock" variant="muted" size={14} className="opacity-60" />}
          </div>
        </div>
      )}
    </div>
  );

  const authForm = React.useMemo(
    () => (isLogin ? <LoginPopup setIsLogin={setIsLogin} /> : <SignUpPopup setIsLogin={setIsLogin} />),
    [isLogin],
  );

  if (onClick) {
    return (
      <>
        <Text
          id={id}
          onClick={authNeeded && !isAuthenticated ? handleAuthClick : onClick}
          css={themedLinkCss}
          className={className}
          title={title}
          style={{
            cursor: authNeeded && !isAuthenticated ? "pointer" : "pointer",
            opacity: authNeeded && !isAuthenticated ? 0.6 : 1,
          }}
        >
          {linkContent}
        </Text>
        {authNeeded && !isAuthenticated && (
          <Dialog open={isModalShow && !(isAuthenticated && nextPath)} onOpenChange={closeModal}>
            {authForm}
          </Dialog>
        )}
      </>
    );
  }

  if (!to) {
    return null;
  }

  if (authNeeded && !isAuthenticated) {
    return (
      <>
        <Text
          id={id}
          onClick={handleAuthClick}
          css={themedLinkCss}
          className={className}
          title={title}
          style={{
            cursor: "pointer",
            opacity: 0.6,
          }}
        >
          {linkContent}
        </Text>
        <Dialog open={isModalShow && !(isAuthenticated && nextPath)} onOpenChange={closeModal}>
          {authForm}
        </Dialog>
      </>
    );
  }

  return (
    <Text.Link id={id} to={to} title={title} className={className} css={themedLinkCss}>
      {linkContent}
    </Text.Link>
  );
};

export default MenuItem;
